import * as React from 'react';

import { User } from '@/features/users';

import { useAuth } from './auth';

export enum ROLES {
  superAdmin = 'superAdmin',
  admin = 'admin',
  manager = 'manager',
  viewer = 'viewer',
}

type RoleTypes = keyof typeof ROLES;

export const POLICIES = {
  'employee:add': (user: User) => {
    if (user.role === ROLES.superAdmin) {
      return true;
    }

    if (user.role === ROLES.admin) {
      return true;
    }

    return false;
  },
  'contractor:add': (user: User) => {
    if (user.role === ROLES.admin) {
      return true;
    }

    if (user.role === ROLES.superAdmin) {
      return true;
    }

    return false;
  },
  'activePaycycle:approve': (user: User) => {
    if (user.role === ROLES.superAdmin) {
      return true;
    }

    return false;
  },
};

export const useAuthorization = () => {
  const { user } = useAuth();

  if (!user?.data) {
    throw Error('User does not exist!');
  }

  const checkAccess = React.useCallback(
    ({ allowedRoles }: { allowedRoles: RoleTypes[] }) => {
      if (allowedRoles && allowedRoles.length > 0) {
        return allowedRoles?.includes(user.data.role);
      }

      return true;
    },
    [user.data.role]
  );

  return { checkAccess, role: user.data.role };
};

type AuthorizationProps = {
  forbiddenFallback?: React.ReactNode;
  children: React.ReactNode;
} & (
  | {
      allowedRoles: RoleTypes[];
      policyCheck?: never;
    }
  | {
      allowedRoles?: never;
      policyCheck: boolean;
    }
);

export const Authorization = ({
  policyCheck,
  allowedRoles,
  forbiddenFallback = null,
  children,
}: AuthorizationProps) => {
  const { checkAccess } = useAuthorization();

  let canAccess = false;

  if (allowedRoles) {
    canAccess = checkAccess({ allowedRoles });
  }

  if (typeof policyCheck !== 'undefined') {
    canAccess = policyCheck;
  }

  return <>{canAccess ? children : forbiddenFallback}</>;
};
